import { LetterAvatarProps } from '../types';
import { getBaseClasses, getColorClasses, truncateText } from '../utils';
import { DEFAULT_COLOR, DEFAULT_THEME, DEFAULT_SIZE } from '../constants';
import { cn } from '@/shared/utils/cn';
import { AvatarStatus } from './AvatarStatus';

/**
 * ============================================
 * LETTER AVATAR - ATOMIC COMPONENT
 * ============================================
 * Avatar que muestra texto (letras, números, símbolos)
 *
 * Casos de uso:
 * - Iniciales de usuario (ej: "JD", "AB")
 * - Contadores (ej: "99+", "100")
 * - Indicadores (ej: "+1", "+5")
 *
 * El texto se trunca automáticamente a 3 caracteres y se convierte a mayúsculas
 *
 * @component
 * @example
 * ```tsx
 * // Iniciales
 * <LetterAvatar text="AB" theme="solid" color="blue" />
 *
 * // Contador
 * <LetterAvatar text="99+" theme="soft" color="red" />
 *
 * // Indicador
 * <LetterAvatar text="+1" theme="solid" color="green" />
 * ```
 */
export const LetterAvatar = ({
  text,
  theme = DEFAULT_THEME,
  color = DEFAULT_COLOR,
  size = DEFAULT_SIZE,
  stroke = false,
  pill = true,
  disabled = false,
  grayscaleWhenDisabled = true,
  className,
  status,
}: LetterAvatarProps) => {
  // Obtener clases de color según el tema
  const colorClasses = getColorClasses(color, theme, 'letter', disabled);

  // Truncar y convertir texto a mayúsculas
  const displayText = truncateText(text);

  // Mapear tamaño del avatar al tamaño del status (solo si no se especifica en status)
  const statusSizeMap: Record<string, 'sm' | 'md' | 'lg'> = {
    xxs: 'sm',
    xs: 'sm',
    sm: 'sm',
    md: 'md',
    lg: 'md',
    xl: 'lg',
  };
  // Usar el size del status si está especificado, sino usar el mapeo automático
  const statusSize = status?.size || statusSizeMap[size] || 'md';

  return (
    <div className="ui-imj ui-relative ui-inline-flex">
      <div
        className={cn(
          getBaseClasses(
            size,
            stroke,
            pill,
            disabled,
            grayscaleWhenDisabled,
            className,
            'letter',
            color,
            theme,
          ),
          colorClasses,
        )}
      >
        {displayText}
      </div>
      {status && <AvatarStatus {...status} size={statusSize} />}
    </div>
  );
};
